import { useQuery, keepPreviousData } from "@tanstack/react-query";

export interface MarketSibling {
  label: string;
  probability: number;
}

export interface HistoryPoint {
  t: number; // unix timestamp seconds
  v: number; // probability 0-100
}

export interface LiveMarket {
  id: string;
  question: string;
  probability: number;
  volume24h: number;
  volumeTotal: string;
  platform: "Polymarket" | "Kalshi";
  category: string;
  slug: string;
  imageUrl?: string;
  endDate?: string;
  sourceUrl: string;
  history: HistoryPoint[];
  siblings?: MarketSibling[];
  topOutcome?: string;
}

interface MarketsResponse {
  markets: LiveMarket[];
  categories: string[];
  fetchedAt: string;
}

async function fetchLiveMarkets(limit = 10, platform?: string, category?: string): Promise<MarketsResponse> {
  const params: Record<string, string> = { limit: String(limit) };
  if (platform) params.platform = platform;
  if (category) params.category = category;
  const queryString = new URLSearchParams(params).toString();

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

  const url = `${supabaseUrl}/functions/v1/markets?${queryString}`;
  const res = await fetch(url, {
    headers: {
      Authorization: `Bearer ${anonKey}`,
      apikey: anonKey,
    },
  });

  if (!res.ok) {
    throw new Error(`Markets API error: ${res.status}`);
  }

  return res.json();
}

export function useLiveMarkets(limit = 10, platform?: string, category?: string) {
  return useQuery({
    queryKey: ["live-markets", limit, platform, category],
    queryFn: () => fetchLiveMarkets(limit, platform, category),
    refetchInterval: 60_000,
    staleTime: 30_000,
    structuralSharing: true,
    placeholderData: keepPreviousData,
  });
}
